import { z } from 'zod';
import {
  AvailabilityOverrideSchema,
  DaysAvailableOverrideSchema,
  SeasonalAvailabilityOverrideSchema,
} from './overrideSchemas';

// Base schema for the ModifierGroupReference model (without refinements)
// Used for embedding in other schemas that may need .partial() operations
export const ModifierGroupReferenceBaseSchema = z.object({
  modifierGroupId: z.number().int({ message: "Modifier Group ID must be an integer" }).readonly(),
  position: z.number().int().default(0).readonly(),
  isRequired: z.boolean().default(false).readonly(),
  minSelection: z.number().min(0).nullable().optional().readonly(),
  maxSelection: z.number().min(0).nullable().optional().readonly(),
  allowMultipleSelection: z.boolean().default(false).readonly(),
  allowDuplicateSelection: z.boolean().default(false).readonly(),
  modifierPriceOverrides: z.array(z.object({
    modifierId: z.number().int(),
    price: z.number(),
  })).optional().readonly(),
  /** A price-only reference the bar created for per-menu prices (removable by the bar). */
  barPriceRef: z.boolean().optional().readonly(),

  // ── Visibility overrides ──
  isHidden: z.boolean().optional().readonly(),
  visibleInPOS: z.boolean().optional().readonly(),
  visibleInKiosk: z.boolean().optional().readonly(),
  visibleInOnlineOrdering: z.boolean().optional().readonly(),
  visibleToPartners: z.boolean().optional().readonly(),
  visibleInInvoices: z.boolean().optional().readonly(),

  // ── Service-type availability override ──
  availability: AvailabilityOverrideSchema.optional().readonly(),

  // ── Scheduling overrides ──
  daysAvailable: DaysAvailableOverrideSchema.optional().readonly(),
  seasonalAvailability: SeasonalAvailabilityOverrideSchema.optional().readonly(),
}).strict();

// Schema with refinements for validation
const ModifierGroupReferenceSchema = ModifierGroupReferenceBaseSchema
.refine(data => !(typeof data.maxSelection !== 'undefined' && typeof data.minSelection !== 'undefined' && data.maxSelection! < data.minSelection!), {
  message: "Maximum selection cannot be less than minimum selection",
  path: ["maxSelection"],
})
.refine(data => !(data.isRequired && (typeof data.minSelection === 'undefined' || data.minSelection! < 1)), {
    message: "Minimum selection must be at least 1 if the group is required",
    path: ["minSelection"],
});

export type ModifierGroupReferenceType = z.infer<typeof ModifierGroupReferenceSchema>;

export default ModifierGroupReferenceSchema;
